// wikiConstants.js — Wiki department metadata + STATIC Tailwind class maps.
// Dynamic classes (`bg-${color}-50`) are purged by Tailwind's JIT, so every
// department-coloured class string is spelled out here in full.

import {
    Building2, Megaphone, ShoppingCart, Headphones, Settings, Monitor,
    DollarSign, Scale, FileText, Folder, FolderOpen, ShieldCheck, Lock,
} from 'lucide-react';

export const WIKI_DEPTS = [
    { id: 'company', name: 'Company', short: 'Company Wiki', color: 'indigo', icon: Building2 },
    { id: 'marketing', name: 'Marketing', short: 'Marketing', color: 'rose', icon: Megaphone },
    { id: 'sales', name: 'Sales', short: 'Sales', color: 'amber', icon: ShoppingCart },
    { id: 'service', name: 'Customer Service', short: 'Customer Service', color: 'cyan', icon: Headphones },
    { id: 'operations', name: 'Operations', short: 'Operations', color: 'violet', icon: Settings },
    { id: 'it', name: 'IT', short: 'IT', color: 'slate', icon: Monitor },
    { id: 'finance', name: 'Finance', short: 'Finance', color: 'emerald', icon: DollarSign },
    { id: 'legal', name: 'Legal', short: 'Legal', color: 'blue', icon: Scale },
];

export const DEPT_BY_ID = Object.fromEntries(WIKI_DEPTS.map(d => [d.id, d]));

// Full class strings per colour family (so JIT keeps them).
export const DEPT_COLORS = {
    indigo: { tile: 'bg-indigo-100 text-indigo-600', icon: 'text-indigo-600', chipOn: 'bg-indigo-50 border-indigo-300 text-indigo-700', cite: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
    rose: { tile: 'bg-rose-100 text-rose-600', icon: 'text-rose-600', chipOn: 'bg-rose-50 border-rose-300 text-rose-700', cite: 'bg-rose-50 border-rose-200 text-rose-700' },
    amber: { tile: 'bg-amber-100 text-amber-600', icon: 'text-amber-600', chipOn: 'bg-amber-50 border-amber-300 text-amber-700', cite: 'bg-amber-50 border-amber-200 text-amber-700' },
    cyan: { tile: 'bg-cyan-100 text-cyan-600', icon: 'text-cyan-600', chipOn: 'bg-cyan-50 border-cyan-300 text-cyan-700', cite: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
    violet: { tile: 'bg-violet-100 text-violet-600', icon: 'text-violet-600', chipOn: 'bg-violet-50 border-violet-300 text-violet-700', cite: 'bg-violet-50 border-violet-200 text-violet-700' },
    slate: { tile: 'bg-stone-100 text-stone-600', icon: 'text-stone-600', chipOn: 'bg-stone-50 border-stone-300 text-stone-700', cite: 'bg-stone-50 border-stone-200 text-stone-700' },
    emerald: { tile: 'bg-emerald-100 text-emerald-600', icon: 'text-emerald-600', chipOn: 'bg-emerald-50 border-emerald-300 text-emerald-700', cite: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    blue: { tile: 'bg-blue-100 text-blue-600', icon: 'text-blue-600', chipOn: 'bg-blue-50 border-blue-300 text-blue-700', cite: 'bg-blue-50 border-blue-200 text-blue-700' },
};

export function deptColors(deptId) {
    return DEPT_COLORS[DEPT_BY_ID[deptId]?.color] || DEPT_COLORS.indigo;
}

export const WIKI_LEVEL_RANK = { none: 0, view: 1, edit: 2, admin: 3 };
export const WIKI_LEVELS = ['none', 'view', 'edit', 'admin'];

// Map the app pillar id → wiki dept id, for department context-opening.
export const PILLAR_TO_WIKI_DEPT = {
    marketing: 'marketing', sales: 'sales', 'customer-service': 'service',
    operations: 'operations', it: 'it', finance: 'finance', legal: 'legal',
    hr: 'company',
};

export const NODE_ICONS = { file: FileText, folder: Folder, folderOpen: FolderOpen, shield: ShieldCheck, lock: Lock };

// Lightweight relative-time formatter for "edited {when}".
export function relativeTime(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const s = Math.max(0, (Date.now() - then) / 1000);
    if (s < 60) return 'just now';
    const m = s / 60; if (m < 60) return `${Math.floor(m)}m ago`;
    const h = m / 60; if (h < 24) return `${Math.floor(h)}h ago`;
    const d = h / 24; if (d < 7) return `${Math.floor(d)}d ago`;
    const w = d / 7; if (w < 5) return `${Math.floor(w)}w ago`;
    const mo = d / 30; if (mo < 12) return `${Math.floor(mo)}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
}
